// Shared mapper for CDA medication import: code resolution, field normalization, and import-to-order prefill.
export const MED_FREQUENCY_OPTIONS = [
  '1 time daily', '2 times daily', '3 times daily', '4 times daily',
  'as directed', 'as needed', 'as one dose', 'at bedtime', 'before every meal',
  'bi-weekly', 'constant infusion', 'daily', 'daily as directed', 'every day',
  'every month', 'every other day', 'every morning', 'every evening', 'every hour',
  'every 2 hours', 'every 3 hours', 'every 4 hours', 'every 4 to 6 hours, as needed',
  'every 4 to 6 minutes', 'every 4 to 8 hours', 'every 6 hours', 'every 8 hours',
  'every 12 hours', 'every 24 hours', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday', 'every Monday, Wednesday, Friday, Sunday',
  'every Tuesday, Thursday, Saturday', 'before breakfast, lunch, dinner',
  'after breakfast, lunch, dinner', 'once a week', 'one time dose', 'three times a week',
  'twice daily', 'two times a week', 'use as directed per instructions in pack', 'weekly',
];

// Dropdown values for route — must stay aligned with CurrentMed and AddMedicationForm.
export const MED_ROUTE_OPTIONS = [
  'Oral', 'Sublingual', 'IVI Intravaginal', 'SQ (Subcutaneous)', 'IM (Intramuscular)',
  'IV (Intravenous)', 'ID (Intradermal)', 'TOP Topical', 'Neb/INH', 'NAS Intranasal',
  'TD Transdermal', 'Urethral', 'Rectally', 'Optic', 'Otic',
];

// Dropdown values for dosage form / dose UOM from CDA imports.
export const MED_DOSAGE_FORM_OPTIONS = [
  'Actuation', 'Ampule', 'Application', 'Applicator', 'Auto-Injector', 'Bar', 'Capful', 'Caplet', 'Capsule',
  'Cartridge', 'Centimeter', 'Disk', 'Dropperful', 'Each', 'Film', 'Fluid Ounce', 'Gallon', 'Gram', 'Gum', 'Implant',
  'Inch', 'Inhalation', 'Injection', 'Insert', 'Liter', 'Lollipop', 'Lozenge', 'Metric Drop', 'Microgram',
  'Milliequivalent', 'Milligram', 'Milliliter', 'Nebule', 'Ounce', 'Package', 'Packet', 'Pad', 'Patch', 'Pellet',
  'Pill', 'Pint', 'Pre-filled Pen Syringe', 'Puff', 'Pump', 'Ring', 'Sachet', 'Scoopful', 'Sponge', 'Spray', 'Stick',
  'Strip', 'Suppository', 'Swab', 'Syringe', 'Tablet', 'Troche', 'Unit', 'Vial', 'Wafer',
];

// Normalize CDA htmlname anchors so import keys match between FE sidebar and BE XML lookup.
function normalizeHtmlname(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.startsWith('#') ? text : `#${text}`;
}

// Strip non-digits from CDA effectiveTime values to build a stable date prefix for matching.
function normalizeDatePrefix(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace(/[^0-9]/g, '').slice(0, 8);
}

// Stable key used to join sidebar med rows with backend import-field responses.
export function medicationImportKey(item) {
  return `${normalizeHtmlname(item?.htmlname)}|${normalizeDatePrefix(item?.lo)}`;
}

// Detect NDC patterns when codeSystem metadata is missing from the CDA scan payload.
function looksLikeNdcCode(code) {
  return /^\d{4,5}-\d{3,4}-\d{1,2}$/.test(code) || /^\d{10,11}$/.test(code);
}

// Detect RxNorm numeric codes when codeSystem metadata is missing from the CDA scan payload.
function looksLikeRxNormCode(code) {
  return /^\d+$/.test(code);
}

// Resolve RxNorm and NDC from CCD code ids, with shape-based fallback when src tags are absent.
export function resolveMedCodesFromCcdIds(codes, ccds) {
  let rxnorm = '';
  let ndc = '';
  if (!Array.isArray(codes) || !ccds) {
    return { rxnorm, ndc };
  }
  for (const ccdId of codes) {
    const ccd = ccds[ccdId];
    if (!ccd || !ccd.code) continue;
    const src = String(ccd.src || '').toLowerCase();
    const code = String(ccd.code).trim();
    if (!code) continue;
    if (!rxnorm && src.includes('rxnorm')) {
      rxnorm = code;
    }
    if (!ndc && src.includes('ndc')) {
      ndc = code;
    }
  }
  // Second pass: infer codes by value shape when explicit RxNorm/NDC src tags were not present.
  if (!ndc || !rxnorm) {
    for (const ccdId of codes) {
      const ccd = ccds[ccdId];
      if (!ccd || !ccd.code) continue;
      const code = String(ccd.code).trim();
      if (!code) continue;
      if (!ndc && looksLikeNdcCode(code)) {
        ndc = code;
      } else if (!rxnorm && looksLikeRxNormCode(code)) {
        rxnorm = code;
      }
    }
  }
  return { rxnorm, ndc };
}

// Case-insensitive match against a dropdown list, preserving unknown values for display.
function findOptionMatch(options, value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (options.includes(text)) return text;
  const lower = text.toLowerCase();
  const match = options.find((option) => option.toLowerCase() === lower);
  return match || text;
}

// Map free-text CDA route strings to our medication route dropdown values.
const ROUTE_ALIASES = {
  oral: 'Oral',
  'by mouth': 'Oral',
  sublingual: 'Sublingual',
  intravenous: 'IV (Intravenous)',
  iv: 'IV (Intravenous)',
  subcutaneous: 'SQ (Subcutaneous)',
  sq: 'SQ (Subcutaneous)',
  intramuscular: 'IM (Intramuscular)',
  im: 'IM (Intramuscular)',
  intradermal: 'ID (Intradermal)',
  topical: 'TOP Topical',
  transdermal: 'TD Transdermal',
  intranasal: 'NAS Intranasal',
  nebulizer: 'Neb/INH',
  inhalation: 'Neb/INH',
  rectal: 'Rectally',
  optic: 'Optic',
  otic: 'Otic',
  intravaginal: 'IVI Intravaginal',
  // CDA imports sometimes use alternate spellings for gastrostomy tube routes.
  'gastrostomy tube': 'Gastrostomy Tube',
  'gastronomy tube': 'Gastrostomy Tube', // common CDA typo
  gastrostomy: 'Gastrostomy Tube',
  gtube: 'Gastrostomy Tube',
  'g-tube': 'Gastrostomy Tube',
};

// Map common CDA dose-unit abbreviations to our dosage-form dropdown values.
const DOSE_UNIT_ALIASES = {
  mg: 'Milligram',
  milligram: 'Milligram',
  milligrams: 'Milligram',
  g: 'Gram',
  gram: 'Gram',
  grams: 'Gram',
  mcg: 'Microgram',
  microgram: 'Microgram',
  micrograms: 'Microgram',
  ml: 'Milliliter',
  milliliter: 'Milliliter',
  milliliters: 'Milliliter',
  l: 'Liter',
  liter: 'Liter',
  tablet: 'Tablet',
  tablets: 'Tablet',
  tab: 'Tablet',
  capsule: 'Capsule',
  capsules: 'Capsule',
  cap: 'Capsule',
  unit: 'Unit',
  units: 'Unit',
  puff: 'Puff',
  puffs: 'Puff',
  patch: 'Patch',
  patches: 'Patch',
};

// Normalize a CDA route string via direct match, alias table, or substring alias lookup.
function mapRoute(route) {
  const text = String(route || '').trim();
  if (!text) return '';
  const direct = findOptionMatch(MED_ROUTE_OPTIONS, text);
  if (MED_ROUTE_OPTIONS.includes(direct)) return direct;
  const alias = ROUTE_ALIASES[text.toLowerCase()];
  if (alias) return alias;
  for (const [key, value] of Object.entries(ROUTE_ALIASES)) {
    if (text.toLowerCase().includes(key)) return value;
  }
  return text;
}

// Normalize a CDA dose unit string to a dosage-form dropdown value.
function mapDoseUnit(unit) {
  const text = String(unit || '').trim();
  // CDA placeholder {tbl} is not a valid dosage form in our dropdown.
  if (!text || text.toLowerCase() === '{tbl}') return '';
  const alias = DOSE_UNIT_ALIASES[text.toLowerCase()];
  if (alias) return findOptionMatch(MED_DOSAGE_FORM_OPTIONS, alias);
  return findOptionMatch(MED_DOSAGE_FORM_OPTIONS, text);
}

// Convert CDA PIVL_TS period attributes and instruction text into a frequency dropdown value.
function mapFrequencyFromPeriod(periodUnit, periodValue, instructions) {
  const unit = String(periodUnit || '').trim().toLowerCase();
  const value = String(periodValue || '').trim();
  const numericValue = value === '' ? null : Number(value);
  const instructionText = String(instructions || '').toLowerCase();

  if (instructionText.includes('as needed') || instructionText.includes('prn')) {
    return 'as needed';
  }
  if (instructionText.includes('at bedtime') || instructionText.includes('at night')) {
    return 'at bedtime';
  }
  if (instructionText.includes('twice daily') || instructionText.includes('two times daily')) {
    return 'twice daily';
  }

  if (unit === 'd' || unit === 'day' || unit === 'days') {
    if (numericValue === 1) return 'daily';
    if (numericValue === 2) return 'every other day';
    if (numericValue === 7) return 'weekly';
  }
  if (unit === 'h' || unit === 'hr' || unit === 'hour' || unit === 'hours') {
    if (numericValue === 1) return 'every hour';
    if (numericValue === 2) return 'every 2 hours';
    if (numericValue === 3) return 'every 3 hours';
    if (numericValue === 4) return 'every 4 hours';
    if (numericValue === 6) return 'every 6 hours';
    if (numericValue === 8) return 'every 8 hours';
    if (numericValue === 12) return 'every 12 hours';
    if (numericValue === 24) return 'every 24 hours';
  }
  if (unit === 'wk' || unit === 'week' || unit === 'weeks') {
    if (numericValue === 1) return 'once a week';
  }

  if (instructionText.includes('once daily') || instructionText.includes('one time daily')) {
    return '1 time daily';
  }
  if (instructionText.includes('every day') || instructionText.includes('once a day')) {
    return 'every day';
  }
  if (instructionText.includes('daily')) {
    return 'daily';
  }

  return '';
}

// Map backend cdaMedicationImport response fields into InsertMedication-ready form values.
export function mapCdaMedImportFields(importFields = {}) {
  const frequency = mapFrequencyFromPeriod(
    importFields.frequencyPeriodUnit,
    importFields.frequencyPeriodValue,
    importFields.instructions
  );
  return {
    doseAmount: String(importFields.doseAmount || '').trim(),
    doseUnit: mapDoseUnit(importFields.doseUnit),
    frequency: frequency ? findOptionMatch(MED_FREQUENCY_OPTIONS, frequency) : '',
    route: mapRoute(importFields.route),
  };
}

// Append a value to a select options list when CDA data uses a value outside our defaults.
export function ensureSelectOption(options, value) {
  const text = String(value || '').trim();
  if (!text) return options;
  if (options.includes(text)) return options;
  return [...options, text];
}

// Prefix added to instructions when a med is imported from a CDA document.
const CDA_IMPORT_PREFIX = '[From CDA]';

// Build a /cdas route path that opens the CDA at the specific medication entry.
export function buildCdaMedicationLink(cdaIndex, itemId) {
  if (cdaIndex === undefined || cdaIndex === null || Number(cdaIndex) < 0) {
    return '';
  }
  if (itemId === undefined || itemId === null || String(itemId).trim() === '') {
    return '';
  }
  return `/cdas/${cdaIndex}/MEDICATION/${itemId}`;
}

// Remove the CDA import tag so ordered meds get clean instruction text.
function stripCdaImportPrefix(instruction) {
  const text = String(instruction || '').trim();
  if (!text.startsWith(CDA_IMPORT_PREFIX)) return text;
  // Strip prefix and any trailing separator (e.g. "[From CDA] - take with food").
  return text.slice(CDA_IMPORT_PREFIX.length).trim().replace(/^\s*[-:]\s*/, '');
}

// True when the med row is an unordered/import entry (order_number 0).
export function isImportedMed(med) {
  if (!med) return false;
  const orderNumber = med.order_number;
  return orderNumber === 0 || orderNumber === '0';
}

// True when the med row belongs to an existing order (order_number !== 0).
export function isOrderedMed(med) {
  if (!med) return false;
  const orderNumber = med.order_number ?? med.ordernumber;
  return orderNumber != null && orderNumber !== 0 && orderNumber !== '0';
}

function parseYearMedTimes(yearmedtime) {
  if (!yearmedtime) return '';
  try {
    const arr = Array.isArray(yearmedtime) ? yearmedtime : JSON.parse(yearmedtime || '[]');
    return arr
      .map((item) => item?.time)
      .filter(Boolean)
      .join(', ');
  } catch {
    return '';
  }
}

// Map a current-med grid row (optionally enriched with MedAdmin record) into edit form shape.
export function mapMedRowToEditFormdata(gridMed, adminMed = null) {
  const source = adminMed || gridMed;
  const prefill = mapMedRowToOrderPrefill(gridMed);
  if (!prefill) return null;

  const administrationTimes =
    prefill.administrationTimes ||
    parseYearMedTimes(source.yearmedtime) ||
    parseYearMedTimes(gridMed.yearmedtime) ||
    '';

  const orderNumber = gridMed.order_number ?? source.order_number ?? source.ordernumber;

  // Prefer explicit admin/prepour/neb flags from the grid row (creation values).
  const administrationFlag =
    source.administration ?? source.medadmin ?? gridMed.administration ?? gridMed.medadmin;
  const prepourFlag = source.prepour ?? gridMed.prepour;
  const nebulizerFlag = source.nebulizer ?? gridMed.nebulizer;

  return {
    ...prefill,
    ...source,
    medentryid: gridMed.medentryid ?? source.medentryid,
    ordernumber: orderNumber,
    order_number: orderNumber,
    medicationName: gridMed.medname || source.medname,
    medname: gridMed.medname || source.medname,
    dosage: gridMed.med_amount || source.med_amount,
    frequency: prefill.med_frequency,
    diagnosis: source.diagnosis || gridMed.diagnose_code || '',
    diagnose_code: gridMed.diagnose_code || source.diagnose_code || '',
    administration: administrationFlag,
    medadmin: administrationFlag,
    prepour: prepourFlag,
    nebulizer: nebulizerFlag,
    administrationTimes,
    time_status_pairs: source.time_status_pairs || gridMed.time_status_pairs || '',
    medholdstartdate: source.medholdstartdate || gridMed.medholdstartdate || '',
    medholdenddate: source.medholdenddate || gridMed.medholdenddate || '',
    status: source.status || gridMed.status || 'active',
    coreason: '',
    providername: source.providername || '',
    providernpi: source.providernpi || '',
    providerdeanumber: source.providerdeanumber || '',
    provideraddress: source.provideraddress || '',
    provideroffice: source.provideroffice || '',
    providercell: source.providercell || '',
    provideremail: source.provideremail || '',
    providerfirstname: source.providerfirstname || '',
    providerlastname: source.providerlastname || '',
    pharmacy: source.pharmacy || source.pharmacyName || source.pharmacyname || '',
    pharmacyNpi: source.pharmacyNpi || '',
    pharmacyDea: source.pharmacyDea || source.pharmdea || '',
    pharmacyAddress: source.pharmacyAddress || source.pharmaddress || '',
    pharmacyPhone: source.pharmacyPhone || source.pharmacyoffice || source.pharmtel || '',
    pharmacyCell: source.pharmacyCell || source.pharmacycell || source.pharmcell || '',
    pharmacyEmail: source.pharmacyEmail || source.pharmacyemail || source.pharmemail || '',
  };
}

// Map a current-med grid row into AddMedicationForm editFormdata shape for ordering.
export function mapMedRowToOrderPrefill(med) {
  if (!med) return null;

  const rawDoseUom = String(med.med_doseuom || '').trim();
  // Fallback {tbl} placeholder to Tablet since it is the most common CDA intent.
  const doseUom = rawDoseUom.toLowerCase() === '{tbl}'
    ? 'Tablet'
    : mapDoseUnit(rawDoseUom);

  // Normalize frequency to a value that exists in the order form dropdown.
  const frequency = med.med_frequency
    ? findOptionMatch(MED_FREQUENCY_OPTIONS, String(med.med_frequency).trim())
    : '';

  const endDate = med.med_enddate || '';
  // Treat sentinel end dates as empty so the order form does not show bogus values.
  const normalizedEndDate =
    !endDate || endDate === '1971-01-01' || endDate === '1970-01-01' ? '' : endDate;

  return {
    medname: med.medname || med.shorthand || '',
    med_amount: med.med_amount || '',
    med_doseuom: doseUom,
    med_frequency: frequency,
    route: mapRoute(med.route), // normalize route aliases from CDA/import data
    ndcnumber: med.ndcnumber || '',
    rxnorns: med.rxnorns || '',
    diagnose_code: med.diagnose_code || '',
    prn: med.prn === 1 || med.prn === '1' ? '1' : '0',
    order_number: med.order_number,
    medadmin: med.medadmin,
    prepour: med.prepour,
    nebulizer: med.nebulizer,
    med_startdate: med.med_startdate || '',
    med_enddate: normalizedEndDate,
    prescription_startdate: med.med_startdate || med.startdate || '', // prescription tab prefill
    prescription_enddate: normalizedEndDate || med.enddate || '',
    startdate: med.med_startdate || med.startdate || '',
    enddate: normalizedEndDate || med.enddate || '',
    instruction: stripCdaImportPrefix(med.instruction), // drop [From CDA] tag for the new order
    link: med.link || '', // carry CDA deep link through import-to-order prefill
    via_med: med.via_med,
    viatype: med.viatype,
    fluidtype: med.fluidtype,
    totalVolumn: med.totalVolumn,
    totalVolumnUnit: med.totalVolumnUnit,
    rate: med.rate,
    ivhowLong: med.ivhowlong, // IV fields carry over when route is intravenous
    ivstarttime: med.ivstarttime,
    ivendtime: med.ivendtime,
    administrationTimes: med.administrationTimes || med.yearmedTimes || '', // prefill admin time slots
  };
}
